import { DatePicker } from 'antd';
import dayjs from 'dayjs';
import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import colorTokens from '../../../constants/design_system/colors';
import { BaseButton } from '../BaseButton';
import CustomTooltip from '../Tooltip/CustomTooltip';
import './CustomDatePicker.scss';

export type DatePickerMode = 'startDate' | 'targetDate';

export type CustomDatePickerProps = {
  onChange: (date: dayjs.Dayjs | null) => void;
  onClose: () => void;
  open?: boolean;
  onOpenChange?: ((open: boolean) => void) | undefined;
  placeholder?: string;
  showFooter?: boolean;
  hideOutline?: boolean;
  extraHeader?: React.ReactNode;
  className?: string;
  startDate?: Date | null;
  targetDate?: Date | null;
  footerTitle?: string;
  mode: DatePickerMode;
};

export const CustomDatePicker = (props: CustomDatePickerProps) => {
  const {
    onChange,
    onClose,
    open,
    onOpenChange,
    showFooter,
    hideOutline,
    placeholder,
    extraHeader,
    startDate,
    targetDate,
    footerTitle,
    mode,
  } = props;

  const [isDropdownOpen, setIsDropdownOpen] = React.useState(open !== undefined ? open : true);
  const currentDate = mode === 'startDate' ? startDate : targetDate;
  // Track the currently viewed month to prevent resetting when navigating
  const [pickerValue, setPickerValue] = React.useState(() => {
    return currentDate ? dayjs(currentDate) : dayjs();
  });
  // Date validation: Start and Target dates cannot be on the same day
  const minDate = mode === 'targetDate' && startDate ? dayjs(startDate).add(1, 'day').toDate() : undefined;
  const maxDate = mode === 'startDate' && targetDate ? dayjs(targetDate).subtract(1, 'day').toDate() : undefined;

  const isTodayDisabled = React.useMemo(() => {
    const today = dayjs();
    if (minDate && today.isBefore(dayjs(minDate), 'day')) {
      return true;
    }
    if (maxDate && today.isAfter(dayjs(maxDate), 'day')) {
      return true;
    }

    return false;
  }, [minDate, maxDate]);

  const getTodayTooltip = () => {
    if (mode === 'startDate') {
      return 'Start date must be before target date';
    }
    return 'Target date must be after start date';
  };

  const getDisabledDateTooltip = () => {
    if (mode === 'startDate') {
      return 'Start date must be before target date';
    }
    return 'Target date must be after start date';
  };

  // Block scroll when datepicker is open
  useEffect(() => {
    if (isDropdownOpen) {
      // Find the nearest scrollable parent (table body or main container)
      const scrollableParent =
        document.querySelector('.ant-table-body') ||
        document.querySelector('[data-scrollable="true"]') ||
        document.body;

      const currentOverflow = (scrollableParent as HTMLElement).style.overflow;

      // Only capture as original if it's not already 'hidden'
      // This prevents race conditions with multiple dropdowns
      const originalOverflow = currentOverflow === 'hidden' ? '' : currentOverflow;

      (scrollableParent as HTMLElement).style.overflow = 'hidden';

      return () => {
        // Restore to original value (empty string allows CSS to take effect)
        (scrollableParent as HTMLElement).style.overflow = originalOverflow;
      };
    }
  }, [isDropdownOpen]);

  const handleOverlayClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    setIsDropdownOpen(false);
    onClose();
  };

  return (
    <>
      {/* Overlay to prevent click-through when datepicker is open.
          Portalled to body so that ancestor transforms (e.g. virtualized rows)
          don't break the fixed positioning. z-index sits above AntD Modal
          content (zIndexPopupBase + 10 = 1010) but below the picker popup. */}
      {isDropdownOpen && createPortal(
        <div
          className="fixed inset-0 z-[1040]"
          style={{ background: 'transparent' }}
          onClick={handleOverlayClick}
          onMouseDown={(e) => {
            e.stopPropagation();
          }}
        />,
        document.body
      )}

      <DatePicker
        className={`${props.className} custom-datepicker flex items-center rounded-none`}
        style={{ outline: !hideOutline ? `2px solid ${colorTokens.green[300]}` : 'none' }}
        placeholder={placeholder ?? 'Set Deadline'}
        picker="date"
        format="MMM DD, YYYY"
        suffixIcon={null}
        allowClear={false}
        autoFocus={true}
        showNow={false}
        inputReadOnly
        mode="date"
        variant="borderless"
        value={currentDate ? dayjs(currentDate) : null}
        onChange={onChange}
        open={isDropdownOpen}
        defaultPickerValue={pickerValue}
        popupStyle={{ zIndex: 1050 }}
        onPanelChange={(value) => {
          if (value) {
            setPickerValue(value);
          }
        }}
        minDate={minDate ? dayjs(minDate) : undefined}
        maxDate={maxDate ? dayjs(maxDate) : undefined}
        size="small"
        onClick={(e) => {
          e.stopPropagation();
          // If dropdown is open and user clicks on input, close it
          if (isDropdownOpen) {
            setIsDropdownOpen(false);
            onClose();
          }
        }}
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            onClose();
          }
        }}
        onOpenChange={(open) => {
          setIsDropdownOpen(open);
          if (onOpenChange) {
            onOpenChange(open);
            return;
          }
          if (!open) onClose();
        }}
        panelRender={(panelNode) => {
          return (
            <div className="custom-datepicker-panel">
              {extraHeader && <div className="m-2">{extraHeader}</div>}
              {/* Add Today button to the header */}
              <div className="custom-header-wrapper">
                {panelNode}
                <BaseButton
                  type="secondary"
                  size="small"
                  className="today-button"
                  disabled={isTodayDisabled}
                  // disabledTooltip={isTodayDisabled ? getTodayTooltip() : undefined}
                  onClick={(e: React.MouseEvent) => {
                    e.stopPropagation();
                    if (!isTodayDisabled) {
                      const today = dayjs();
                      onChange(today);
                      onClose();
                    }
                  }}
                >
                  Today
                </BaseButton>
              </div>
            </div>
          );
        }}
        cellRender={(current, info) => {
          if (info.type !== 'date') {
            return info.originNode;
          }
          if (typeof current === 'number' || typeof current === 'string') {
            return <div className="ant-picker-cell-inner">{current}</div>;
          }

          const isToday = current.isSame(dayjs(), 'day');
          const isCurrentStartDate = startDate && current.isSame(dayjs(startDate), 'day');
          const isCurrentTargetDate = targetDate && current.isSame(dayjs(targetDate), 'day');

          // Check if date is disabled
          const isDisabled =
            (minDate && current.isBefore(dayjs(minDate), 'day')) || (maxDate && current.isAfter(dayjs(maxDate), 'day'));

          let cellStyle: React.CSSProperties = {};

          if (isCurrentStartDate) {
            cellStyle = {
              backgroundColor: colorTokens.blue[600],
            };
          } else if (isCurrentTargetDate) {
            cellStyle = {
              backgroundColor: colorTokens.red[600],
            };
          }

          const cellContent = (
            <div className={`flex`} title="">
              <div
                className={`flex items-center justify-center w-6 h-6 text-sm font-normal ${
                  isCurrentStartDate || isCurrentTargetDate ? 'text-white rounded-full' : ''
                } ${isToday && !isCurrentStartDate && !isCurrentTargetDate ? 'text-blue-600 font-semibold' : ''}`}
                style={{ ...cellStyle }}
              >
                {current.date()}
              </div>
            </div>
          );

          if (isDisabled) {
            return <CustomTooltip title={getDisabledDateTooltip()}>{cellContent}</CustomTooltip>;
          }

          return cellContent;
        }}
        renderExtraFooter={() => {
          if (currentDate === null && showFooter !== true) {
            return <></>;
          }
          return (
            <div
              className="h-6 w-full px-1 py-0.5 hover:bg-neutral-200 rounded justify-center items-center gap-2 inline-flex cursor-pointer"
              onClick={() => {
                onChange(null);
                onClose();
              }}
            >
              <div className="text-brand-grey-dark text-sm font-normal leading-tight">
                {footerTitle ?? 'Remove Date'}
              </div>
            </div>
          );
        }}
      />
    </>
  );
};
